/**
 * The EU legislative process drawn as a metro line: the stations a draft food
 * act passes through, the two lines it can ride, and where every pipeline
 * stage sits on the map. Used by the explainer pop-up and the live map on
 * /legislation/pipeline.
 *
 * Line S = regulatory procedure with scrutiny (Parliament and Council may
 * block the act for 3 months after the committee vote); Line E = examination
 * procedure (straight from the vote to adoption). The procedure comes from
 * the register's `legislativeProcedure` label on the dossier.
 */

export type StationId = 'feedback' | 'tabled' | 'vote' | 'scrutiny' | 'adopted' | 'published' | 'applies';
export type LineId = 'S' | 'E';
/** 0 = the text can still change, 1 = yes or no only, 2 = final text. */
export type Zone = 0 | 1 | 2;

export interface Station {
  id: StationId;
  name: string;
  sub: string;
  zone: Zone;
  /** On the Line S loop (only scrutiny). */
  branch: boolean;
  lines: LineId[];
  /** Dossier stages counted at this station. */
  stages: string[];
  /** Icon paths, 24x24 stroke. */
  icon: string[];
  /** The metro side of the story. */
  metro: string;
  metroText: string;
  /** What actually happens. */
  realTitle: string;
  realText: string;
  who: string;
  time: string;
  change: string;
  changeTone: 'open' | 'limited' | 'closed';
}

export const STATIONS: Station[] = [
  {
    id: 'feedback', name: 'Feedback', sub: 'Have Your Say', zone: 0, branch: false, lines: ['S', 'E'],
    stages: ['feedback_open'],
    icon: ['M4 5h16v11H9l-5 4z', 'M8 9.5h8M8 12.5h5'],
    metro: 'Boarding',
    metroText: 'The draft boards at the first station. Anyone on the platform can tell the driver what they think before the train leaves.',
    realTitle: 'Feedback on Have Your Say',
    realText: 'The Commission (DG SANTE) publishes the draft act on the Have Your Say portal. Companies, associations and citizens can send feedback.',
    who: 'Commission, open to anyone', time: '4 weeks', change: 'Yes, freely', changeTone: 'open',
  },
  {
    id: 'tabled', name: 'Tabled', sub: 'PAFF committee', zone: 0, branch: false, lines: ['S', 'E'],
    // Not 'in_progress': that is the "stage not known" default, off the
    // /news stage strip too, so those dossiers only show under All.
    stages: ['tabled'],
    icon: ['M3 14h18', 'M6 14v5M18 14v5', 'M8 10.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM16 10.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4z'],
    metro: 'The committee platform',
    metroText: 'The train stops at the committee platform, often more than once. Inspectors from 27 countries check it, and carriages get swapped between stops.',
    realTitle: 'Tabled at the PAFF committee',
    realText: 'The draft goes on the agenda of a section of the PAFF committee, where experts from the 27 member states discuss it. It often takes several meetings and versions.',
    who: '27 member-state experts', time: 'One or more meetings', change: 'Yes, version by version', changeTone: 'open',
  },
  {
    id: 'vote', name: 'Vote', sub: 'Qualified majority', zone: 0, branch: false, lines: ['S', 'E'],
    stages: ['voted_positive', 'voted_negative', 'no_opinion'],
    icon: ['M4 12h16v8H4z', 'M8 12V4h8v8', 'M10 8l1.5 1.5L14.5 6.5'],
    metro: 'The junction signal',
    metroText: 'A green signal needs 15 of the 27 signal boxes, together serving 65% of the passengers. Red, or no signal at all, sends the train back to the depot.',
    realTitle: 'The committee votes',
    realText: 'Member states vote by qualified majority: at least 55% of countries (15 of 27) representing at least 65% of the EU population. A negative or no opinion sends the draft back to the Commission or to the appeal committee.',
    who: '27 member states', time: 'One meeting', change: 'Last chance to amend', changeTone: 'limited',
  },
  {
    id: 'scrutiny', name: 'Scrutiny', sub: 'Parliament + Council', zone: 1, branch: true, lines: ['S'],
    stages: ['scrutiny'],
    icon: ['M10.5 17a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13z', 'M15.5 15.5 20 20'],
    metro: 'Line S waits here',
    metroText: 'Line S trains wait here for up to three months. Parliament and Council can stop the train, but not rebuild it. Line E trains run straight past on the express track.',
    realTitle: 'Scrutiny by Parliament and Council',
    realText: 'Acts under the regulatory procedure with scrutiny (pesticide MRLs, food contact materials, microbiological criteria) wait 3 months. The European Parliament or the Council can block the act, but not amend it.',
    who: 'European Parliament and Council', time: '3 months', change: 'Block only, no edits', changeTone: 'limited',
  },
  {
    id: 'adopted', name: 'Adopted', sub: 'Commission', zone: 2, branch: false, lines: ['S', 'E'],
    stages: ['adopted'],
    icon: ['M9 4h6v5l2 3H7l2-3z', 'M5 15h14v3H5z', 'M7 21h10'],
    metro: 'Doors close',
    metroText: 'The doors close and the train is sealed: nothing gets on or off any more.',
    realTitle: 'Adopted by the Commission',
    realText: 'The Commission formally adopts the act. From here on, the text is final.',
    who: 'European Commission', time: 'A few weeks', change: 'No', changeTone: 'closed',
  },
  {
    id: 'published', name: 'Published', sub: 'Official Journal', zone: 2, branch: false, lines: ['S', 'E'],
    stages: ['published'],
    icon: ['M5 4h11a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2z', 'M5 18a2 2 0 0 1 2-2h11', 'M9 8h5M9 11h5'],
    metro: 'On the departure board',
    metroText: 'The train appears on the departure board. Twenty days later it enters service.',
    realTitle: 'Published in the Official Journal',
    realText: 'The act is published in the Official Journal of the EU and usually enters into force on the twentieth day after publication.',
    who: 'Publications Office', time: 'In force after 20 days', change: 'No', changeTone: 'closed',
  },
  {
    id: 'applies', name: 'Applies', sub: 'End of the line', zone: 2, branch: false, lines: ['S', 'E'],
    stages: ['applies'],
    icon: ['M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z', 'M9 12l2 2 4-4'],
    metro: 'End of the line',
    metroText: 'Everyone gets off. From this date the rules apply to every food business, and the inspectors know it.',
    realTitle: 'The act applies',
    realText: 'The date from which you must comply. It can be months after entry into force, with transition periods for products already on the market.',
    who: 'Every food business', time: 'Date set in the act', change: 'No: time to comply', changeTone: 'closed',
  },
];

export const ZONE_LABELS: Record<Zone, string> = {
  0: 'Text can change',
  1: 'Yes or no only',
  2: 'Final text',
};

export const LINE_LABELS: Record<LineId, { name: string; long: string }> = {
  S: { name: 'Line S', long: 'with scrutiny: pesticide MRLs, food contact materials, microbiological criteria' },
  E: { name: 'Line E', long: 'examination: feed additives, import conditions, official controls' },
};

const STATION_BY_STAGE: Record<string, StationId> = Object.fromEntries(
  STATIONS.flatMap(s => s.stages.map(stage => [stage, s.id])),
);

/** The station a dossier stage sits at; null for stages off the line (withdrawn). */
export function stationOf(stage: string): StationId | null {
  return STATION_BY_STAGE[stage] ?? null;
}

/** The line a dossier rides, from its register procedure label; null until known. */
export function lineOf(procedure: string | null | undefined): LineId | null {
  if (!procedure) return null;
  if (procedure.includes('with_scrutiny')) return 'S';
  if (procedure.includes('examination')) return 'E';
  return null;
}

export interface StationCount {
  total: number;
  S: number;
  E: number;
}

export type StationCounts = Record<StationId, StationCount>;

/** Dossiers per station and line. Dossiers off the line are not counted. */
export function countByStation(items: Array<{ stage: string; legislative_procedure: string | null }>): StationCounts {
  const counts = Object.fromEntries(STATIONS.map(s => [s.id, { total: 0, S: 0, E: 0 }])) as StationCounts;
  for (const item of items) {
    const station = stationOf(item.stage);
    if (!station) continue;
    counts[station].total += 1;
    const line = lineOf(item.legislative_procedure);
    if (line) counts[station][line] += 1;
  }
  return counts;
}

export function stationIndex(id: StationId): number {
  return STATIONS.findIndex(s => s.id === id);
}
